import { useQuery } from '@tanstack/react-query';

import axios from 'utils/axios';
import { downloadCsv } from 'utils/download';
import { Customer, LedgerEntry, Me, Overview, PayoutsInfo, Statement, StatementSummary } from 'types/partner';

// The dashboard is read-only. Every call carries only the partner token and the backend
// scopes each answer to that partner, so no id is ever sent from here.
const BASE = '/api/v1/partner';

const get = async <T>(url: string, params?: Record<string, string | undefined>) => (await axios.get<T>(`${BASE}${url}`, { params })).data;

export const useMe = () => useQuery({ queryKey: ['partner', 'me'], queryFn: () => get<Me>('/me'), staleTime: 5 * 60_000 });

export const useOverview = () => useQuery({ queryKey: ['partner', 'overview'], queryFn: () => get<Overview>('/overview') });

export const useCustomers = () =>
  useQuery({ queryKey: ['partner', 'customers'], queryFn: async () => (await get<{ customers: Customer[] }>('/customers')).customers });

export type CommissionFilters = { period?: string; status?: string };

export const useCommissions = (filters: CommissionFilters) =>
  useQuery({
    queryKey: ['partner', 'commissions', filters],
    queryFn: async () => (await get<{ entries: LedgerEntry[] }>('/commissions', filters)).entries
  });

export const useStatements = () =>
  useQuery({ queryKey: ['partner', 'statements'], queryFn: async () => (await get<{ statements: StatementSummary[] }>('/statements')).statements });

export const useStatement = (period: string) =>
  useQuery({ queryKey: ['partner', 'statement', period], queryFn: () => get<Statement>(`/statements/${period}`), enabled: !!period });

export const usePayouts = () => useQuery({ queryKey: ['partner', 'payouts'], queryFn: () => get<PayoutsInfo>('/payouts') });

export const downloadCommissionsCsv = (period?: string) =>
  downloadCsv(`${BASE}/commissions.csv`, `commissions${period ? `-${period}` : ''}.csv`, { period });

export const downloadStatementCsv = (period: string) => downloadCsv(`${BASE}/statements/${period}/csv`, `statement-${period}.csv`);
